import classNames from 'classnames';
import { FunctionComponent, useState, useRef, useEffect } from 'react';

import { ASX_USER_LOCALE } from 'src/constants';
import { useDropdownContext } from 'src/contexts/DropdownContext';
import { THEME } from 'src/enums/theme';
import useDeviceSize from 'src/hooks/useDeviceSize';
import CheckIcon from 'src/icons/check.svg';
import ChevronDownIcon from 'src/icons/chevron-down.svg';

import styles from './LocalePicker.module.scss';

const DEFAULT_LOCALE = 'en-US';
const MONS_LOCALE_QUERY_PARAM = '?mons_sel_locale=';

// TODO: Add/Review full list of P0 Locales
const LABELS_BY_LOCALE: Record<string, string> = {
  'en-US': 'English (US)',
  'es-ES': 'Español (ES)',
  'es-MX': 'Español (MX)',
  'fr-FR': 'Français (FR)',
  'it-IT': 'Italiano (IT)',
  'de-DE': 'Deutsch (DE)',
  'ja-JP': '日本語 (日本)',
  'pl-PL': 'Polski (PL)',
  'pt-BR': 'Português (BR)',
  'pt-PT': 'Português (PT)',
  'zh-CN': '普通话 (中国)',
};

interface LocaleOptionProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * Component for individual locale option row in dropdown.
 */
const LocaleOption: FunctionComponent<LocaleOptionProps> = ({ label, isSelected, onClick }) => (
  <div
    className={classNames(styles.dropdownOption, { [styles.selected]: isSelected })}
    onClick={onClick}
    onKeyDown={(e) => e.key === 'Enter' && onClick()}
    role="option"
    aria-selected={isSelected}
    tabIndex={0}
  >
    {label}
    {isSelected && (
      <span className={styles.checkContainer}>
        <CheckIcon className={styles.checkIcon} />
      </span>
    )}
  </div>
);

type LocalePickerVariant = 'primary' | 'secondary';
interface LocalePickerProps {
  /** Variant describes the style of the locale picker when the sub-nav or mobile-nav menus are expanded. "primary" variant for collapsed and "secondary" for expanded. */
  variant: LocalePickerVariant;
  /** Theme describes the style of the locale picker against the main theme of the navbar */
  theme?: THEME;
}

/**
 * Component for locale picker button and dropdown.
 */
export const LocalePicker: FunctionComponent<LocalePickerProps> = ({ variant = 'primary', theme = THEME.LIGHT }) => {
  const { isSmallDesktop } = useDeviceSize();
  const { isDropdownOpen, toggleDropdown, closeDropdown, registerDropdownRef } = useDropdownContext();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [currLocale, setCurrLocale] = useState(localStorage.getItem(ASX_USER_LOCALE) ?? DEFAULT_LOCALE);

  const isExpanded = isDropdownOpen('locale-picker');

  // Register the dropdown ref for outside click detection
  useEffect(() => {
    registerDropdownRef('locale-picker', dropdownRef.current);
    return () => registerDropdownRef('locale-picker', null);
  }, [registerDropdownRef]);

  // Only apply the secondary (expanded) variant if the navbar is not dark themed. When dark themed, the collapsed and expanded variants are the same style.
  const isSecondary = variant === 'secondary' && theme !== THEME.DARK;

  /**
   * On locale change, set localStorage value on browser and redirect with Mons Selection API
   * https://w.amazon.com/bin/view/Mons/Selections/#HMonsLanguageAPI
   */
  const handleChange = (localeCode: string) => {
    setCurrLocale(localeCode);
    localStorage.setItem(ASX_USER_LOCALE, localeCode);
    closeDropdown();
    // Convert locale string to Mons format and invoke redirect URL to set cookie on future requests.
    const monsLocale = localeCode.replace('-', '_');
    window.location.assign(`${window.location.pathname}${MONS_LOCALE_QUERY_PARAM}${monsLocale}`);
  };

  const desktopDropdown = (
    <div
      className={classNames(styles.dropdown, styles.show, styles[theme])}
      onMouseEnter={() => {
        // Keep dropdown open when mouse enters
      }}
      onMouseLeave={() => {
        closeDropdown();
      }}
    >
      {Object.entries(LABELS_BY_LOCALE).map(([localeCode, label]) => (
        <LocaleOption
          key={localeCode}
          label={label}
          isSelected={currLocale === localeCode}
          onClick={() => handleChange(localeCode)}
        />
      ))}
    </div>
  );

  const mobileDropdown = (
    <div className={classNames(styles.mobileSelectWrapper, styles[theme])}>
      <select
        aria-label="locale-picker"
        className={classNames(styles.mobileSelect)}
        data-testid="mobile-locale-picker"
        onChange={(event) => handleChange(event.target.value)}
        value={currLocale}
      >
        {Object.entries(LABELS_BY_LOCALE).map(([localeCode, label]) => (
          <option key={localeCode} label={label} value={localeCode} onClick={() => handleChange(localeCode)} />
        ))}
      </select>
    </div>
  );

  return isSmallDesktop ? (
    <div ref={dropdownRef}>
      {/* Locale Picker Button */}
      <div
        role="menu"
        tabIndex={0}
        aria-label="locale-picker"
        className={classNames(styles.localePicker, styles[theme], {
          [styles.secondary]: isSecondary,
        })}
        onClick={() => {
          toggleDropdown('locale-picker');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleDropdown('locale-picker');
          }
        }}
      >
        <span className={styles.localeLabel}>{LABELS_BY_LOCALE[currLocale]}</span>
        <span>
          <ChevronDownIcon className={classNames(styles.localePickerIcon, { [styles.rotated]: isExpanded })} />
        </span>
      </div>

      {/* Locale Picker Dropdown */}
      {isExpanded && desktopDropdown}
    </div>
  ) : (
    mobileDropdown
  );
};
